import { create } from 'zustand';

interface ActiveTask {
  taskId: string;
  type: string;
  status: string;
  progress: number;
  phase: string;
}

export interface TaskLifecycleSnapshot {
  taskId: string;
  status: string;
  type: string;
  progress: number;
  phase: string;
  error?: string;
  timestamp: number;
}

interface TaskState {
  activeTasks: Record<string, ActiveTask>;
  lastLifecycleEvent: TaskLifecycleSnapshot | null;
  lifecycleByTaskId: Record<string, TaskLifecycleSnapshot>;
  reconciliationVersion: number;
  applyLifecycleEvent: (event: TaskLifecycleSnapshot) => void;
  reconcileActiveTasks: (
    tasks: Array<Pick<ActiveTask, 'taskId' | 'type' | 'status' | 'progress' | 'phase'>>,
    requestedAt: number,
  ) => void;
  recordLifecycleEvent: (event: TaskLifecycleSnapshot) => void;
  upsertTask: (taskId: string, data: Partial<ActiveTask> & { taskId: string }) => void;
  removeTask: (taskId: string) => void;
  clearAll: () => void;
}

const TERMINAL_STATUSES = new Set(['completed', 'success', 'failed', 'cancelled', 'canceled']);
const STATUS_RANK: Record<string, number> = {
  queued: 0,
  running: 1,
  completed: 2,
  success: 2,
  failed: 2,
  cancelled: 2,
  canceled: 2,
};

export const useTaskStore = create<TaskState>((set) => ({
  activeTasks: {},
  lastLifecycleEvent: null,
  lifecycleByTaskId: {},
  reconciliationVersion: 0,
  applyLifecycleEvent: (event) => set((state) => {
    const status = event.status.toLowerCase();
    const previous = state.lifecycleByTaskId[event.taskId];
    const previousStatus = previous?.status.toLowerCase();
    if (previousStatus && (
      TERMINAL_STATUSES.has(previousStatus)
      || (STATUS_RANK[status] ?? -1) < (STATUS_RANK[previousStatus] ?? -1)
      || ((STATUS_RANK[status] ?? -1) === (STATUS_RANK[previousStatus] ?? -1)
        && event.timestamp < previous.timestamp)
    )) {
      return state;
    }

    const nextActiveTasks = { ...state.activeTasks };
    if (TERMINAL_STATUSES.has(status)) {
      delete nextActiveTasks[event.taskId];
    } else if (status === 'queued' || status === 'running') {
      nextActiveTasks[event.taskId] = {
        taskId: event.taskId,
        type: event.type,
        status,
        progress: event.progress,
        phase: event.phase,
      };
    }

    return {
      activeTasks: nextActiveTasks,
      lastLifecycleEvent: { ...event, status },
      lifecycleByTaskId: {
        ...state.lifecycleByTaskId,
        [event.taskId]: { ...event, status },
      },
    };
  }),
  reconcileActiveTasks: (tasks, requestedAt) => set((state) => {
    const activeTasks: Record<string, ActiveTask> = {};
    for (const task of tasks) {
      const latest = state.lifecycleByTaskId[task.taskId];
      if (latest && latest.timestamp > requestedAt && TERMINAL_STATUSES.has(latest.status)) {
        continue;
      }
      activeTasks[task.taskId] = task;
    }
    for (const [taskId, task] of Object.entries(state.activeTasks)) {
      const latest = state.lifecycleByTaskId[taskId];
      if (latest && latest.timestamp > requestedAt && !TERMINAL_STATUSES.has(latest.status)) {
        activeTasks[taskId] = task;
      }
    }
    return {
      activeTasks,
      reconciliationVersion: state.reconciliationVersion + 1,
    };
  }),
  recordLifecycleEvent: (event) => set({ lastLifecycleEvent: event }),
  upsertTask: (taskId, data) =>
    set((state) => ({
      activeTasks: {
        ...state.activeTasks,
        [taskId]: { ...state.activeTasks[taskId], ...data, taskId },
      },
    })),
  removeTask: (taskId) =>
    set((state) => {
      const next = { ...state.activeTasks };
      delete next[taskId];
      return { activeTasks: next };
    }),
  clearAll: () => set({
    activeTasks: {},
    lastLifecycleEvent: null,
    lifecycleByTaskId: {},
    reconciliationVersion: 0,
  }),
}));
